import { useRef, type ReactElement, type ReactNode } from "react";
import { BODY_TYPE } from "odyssey-shared";
import type { ModerationAction } from "odyssey-shared";
import type { EnKey } from "odyssey-lang";
import { Button, Glyph, KIT_METRICS, PendingSpinner, Segmented, centreOffset, TextArea, fieldFrame, frameClass, logical, useInteraction, useLogicalSize, type GlyphName } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import { MODERATION_LAYOUT, chosenMinutes, type TimedAction } from "./layout.ts";

/**
 * The fields of the action chosen in the Moderation window (ADR-056's amendment of 2026-10-06, decision 14; the
 * design's `moderation-window` card, a visual reference): Length for a freeze, a ban and a feedback pause alone; the
 * two reasons, the player's and the staff's, told apart by their frame, their glyph and their words; and the footer
 * pinned under the pane, saying who records it, with Cancel and the named action. A Flag has one box, the staff's,
 * which is the action's reason on the wire; every other action's player box is its reason and its staff box its
 * note. The rules and the numbers are ours: a reason and a note of up to 200 characters, a freeze of 1 to 60
 * minutes, a ban of up to 30 days or permanent, a pause of up to 30 days.
 */

/**
 * The lengths the form offers for each timed action: the value the form holds and the key of its label, in the
 * order shown. `perm` is a ban that never ends and `h` a pause of one hour; the rest count the action's own unit.
 */
export const LENGTH_PRESETS = {
  freeze: [
    ["5", "moderation.window.presets.freeze.min5"],
    ["15", "moderation.window.presets.freeze.min15"],
    ["30", "moderation.window.presets.freeze.min30"],
    ["60", "moderation.window.presets.freeze.min60"],
  ],
  ban: [
    ["1", "moderation.window.presets.ban.day1"],
    ["3", "moderation.window.presets.ban.days3"],
    ["7", "moderation.window.presets.ban.days7"],
    ["30", "moderation.window.presets.ban.days30"],
    ["perm", "moderation.window.presets.ban.permanent"],
  ],
  feedbackpause: [
    ["h", "moderation.window.presets.feedbackpause.hour1"],
    ["1", "moderation.window.presets.feedbackpause.day1"],
    ["7", "moderation.window.presets.feedbackpause.days7"],
    ["30", "moderation.window.presets.feedbackpause.days30"],
  ],
} as const satisfies Readonly<Record<TimedAction, readonly (readonly [string, EnKey])[]>>;

/**
 * The form's text: the length chosen, the player's reason and the staff's note.
 */
export interface FormText {
  readonly length: string;
  readonly reason: string;
  readonly note: string;
}

/**
 * Length: the presets and the length typed in the action's unit, and when it would end.
 *
 * @param props The `action`; the `value` chosen or typed; `onChange`; `disabled` while recording; `ends`, the
 *   sentence of when it would end, or null when the length is not one the action takes; and `permanent`, whether the
 *   account may ban permanently, without which the Permanent choice is not drawn (the author's ruling of 2026-10-08).
 * @returns The field.
 */
export function LengthField({ action, value, onChange, disabled, ends, permanent = false }: { readonly action: TimedAction; readonly value: string; readonly onChange: (value: string) => void; readonly disabled: boolean; readonly ends: string | null; readonly permanent?: boolean }): ReactElement {
  const { state, handlers } = useInteraction({ disabled, focus: "any" });
  const presets = LENGTH_PRESETS[action].filter(([key]) => key !== "perm" || permanent);
  const preset = presets.some(([key]) => key === value) ? value : null;
  const frame = fieldFrame({ ...state, disabled, error: ends === null });
  return (
    <div className="ot-moderation__length" data-moderation="length">
      <span className="ot-moderation__label ot-moderation__muted">{say("moderation.window.length")}</span>
      <div className="ot-moderation__length-row">
        <Segmented label={say("moderation.window.length")} value={preset} onChange={(next) => !disabled && onChange(next)} options={presets.map(([key, label]) => ({ value: key, label: say(label) }))} />
        <span className="ot-moderation__length-own">
          <span className="ot-moderation__muted ot-moderation__small" style={{ marginTop: logical(centreOffset(KIT_METRICS.field.boxHeight, BODY_TYPE["body-sm"].lineHeight)) }}>
            {say("moderation.window.or")}
          </span>
          <span className={`ot-field__box ot-moderation__length-box ${frameClass(frame)}`} style={{ height: logical(KIT_METRICS.field.boxHeight), width: logical(MODERATION_LAYOUT.lengthField) }}>
            <input className="ot-field__input" aria-label={say("moderation.window.lengthIn", { length: { k: "moderation.window.length" }, unit: { k: `moderation.window.units.${action}` } })} value={value === "perm" || value === "h" ? "" : value} maxLength={3} inputMode="numeric" disabled={disabled} spellCheck={false} onChange={(event) => onChange(event.currentTarget.value)} onFocus={handlers.onFocus} onBlur={handlers.onBlur} style={{ padding: `${logical(KIT_METRICS.field.paddingY)} ${logical(KIT_METRICS.field.paddingX)}` }} />
          </span>
          <span className="ot-moderation__muted ot-moderation__small" style={{ marginTop: logical(centreOffset(KIT_METRICS.field.boxHeight, BODY_TYPE["body-sm"].lineHeight)) }}>
            {say(`moderation.window.units.${action}`)}
          </span>
        </span>
      </div>
      <p className={ends === null ? "ot-moderation__line ot-moderation__danger" : "ot-moderation__line"} data-moderation="ends">
        {ends ?? say("moderation.window.lengthOff")}
      </p>
    </div>
  );
}

/**
 * One reason's box: the player's, framed in brass with the eye, or the staff's, plain with the lock; its words
 * under it.
 *
 * @param props `kind`; the box's `label`; its `value` and `onChange`; and `disabled` while recording.
 * @returns The box.
 */
export function ReasonBox({ kind, label, value, onChange, disabled }: { readonly kind: "player" | "staff"; readonly label: string; readonly value: string; readonly onChange: (value: string) => void; readonly disabled: boolean }): ReactElement {
  const glyph: GlyphName = kind === "player" ? "eye-small" : "lock";
  return (
    <div className={`ot-moderation__reason ot-moderation__reason--${kind}`} data-moderation={`reason-${kind}`}>
      <TextArea label={label} value={value} onChange={onChange} lines={2} maxLength={MODERATION_LAYOUT.textLimit} counter disabled={disabled} />
      <p className="ot-moderation__hint">
        <span className="ot-moderation__hint-mark">
          <Glyph name={glyph} />
        </span>
        <span>{kind === "player" ? say("moderation.window.playerHint") : say("moderation.window.staffHint")}</span>
      </p>
    </div>
  );
}

/**
 * The fields an action takes: its note, Length when it takes one, and its reasons, side by side in the wide window,
 * the first column a whole number of logical pixels so neither box lands between pixels, and stacked in the narrow
 * one.
 *
 * @param props The `action`; the `family` it is on; the form's `text` and `onText`; `disabled` while recording;
 *   `narrow`; `ends`, when the length would end, or null when it is not one the action takes; and the `refused`
 *   sentence, shown above the footer, or null; and whether the account may ban `permanent`ly.
 * @returns The fields.
 */
export function ActionFields({ action, family, text, onText, disabled, narrow, ends, refused, permanent }: { readonly action: ModerationAction; readonly family: string; readonly text: FormText; readonly onText: (text: Partial<FormText>) => void; readonly disabled: boolean; readonly narrow: boolean; readonly ends: string | null; readonly refused: string | null; readonly permanent: boolean }): ReactElement {
  const note = action === "warn" ? say("moderation.window.notes.warn", { family }) : action === "alert" ? say("moderation.window.notes.alert", { family }) : action === "kick" || action === "freeze" || action === "ban" ? say(`moderation.window.notes.${action}`) : null;
  const timed = action === "freeze" || action === "ban";
  const pair = useRef<HTMLDivElement>(null);
  const size = useLogicalSize(pair, narrow);
  const first = size === null || narrow ? null : Math.floor((Math.floor(size.width) - MODERATION_LAYOUT.gap) / 2);
  return (
    <div className="ot-moderation__form" data-moderation="form">
      {note === null ? null : <p className="ot-moderation__muted ot-moderation__text">{note}</p>}
      {timed ? <LengthField action={action} value={text.length} onChange={(length) => onText({ length })} disabled={disabled} ends={ends} permanent={permanent} /> : null}
      {action === "alert" ? (
        <ReasonBox kind="staff" label={say("moderation.window.flagTitle")} value={text.reason} onChange={(reason) => onText({ reason })} disabled={disabled} />
      ) : (
        <div ref={pair} className={narrow ? "ot-moderation__reasons ot-moderation__reasons--stacked" : "ot-moderation__reasons"} style={first === null ? undefined : { gridTemplateColumns: `${logical(first)} minmax(0, 1fr)` }}>
          <ReasonBox kind="player" label={say("moderation.window.playerReads", { family })} value={text.reason} onChange={(reason) => onText({ reason })} disabled={disabled} />
          <ReasonBox kind="staff" label={say("moderation.window.staffOnly")} value={text.note} onChange={(value) => onText({ note: value })} disabled={disabled} />
        </div>
      )}
      {refused === null ? null : <Refused sentence={refused} />}
    </div>
  );
}

/**
 * The gateway's refusal, kept above the footer with every field as it was.
 *
 * @param props The `sentence`.
 * @returns The line.
 */
export function Refused({ sentence }: { readonly sentence: string }): ReactElement {
  return (
    <div className="ot-moderation__refused" role="alert" data-moderation="error">
      <span className="ot-moderation__refused-mark">
        <Glyph name="alert" />
      </span>
      <span>{say("moderation.window.refused", { sentence })}</span>
    </div>
  );
}

/**
 * The size of the pending spinner in logical pixels, which the footer centres on its buttons' height.
 */
const SPINNER_SIZE = 8;

/**
 * The footer pinned under the pane: who records it, Cancel, and the named action, which reads Try again after a
 * refusal and shows the busy dots while recording. While recording the left of the footer says nothing until the
 * gateway has gone `PENDING_SPINNER_DELAY_MS` without an answer, then holds the kit's pending spinner, named
 * "Recording: waiting for the server." for a screen reader (P2.8-T41, the author's choice of 2026-10-09).
 *
 * @param props `self`, the GM's family or null when the page does not know it; the `label` of the action's button;
 *   whether it is `danger`; `busy`; whether the wait is `slow`; `refused`; and `onCancel` and `onGo`.
 * @returns The footer.
 */
export function ActionFooter({ self, label, danger, busy, slow, refused, onCancel, onGo }: { readonly self: string | null; readonly label: string; readonly danger: boolean; readonly busy: boolean; readonly slow: boolean; readonly refused: boolean; readonly onCancel: () => void; readonly onGo: () => void }): ReactElement {
  const go: ReactNode = refused ? say("moderation.window.tryAgain") : label;
  return (
    <div className="ot-moderation__footer" data-moderation="footer">
      {busy && slow ? (
        <span className="ot-moderation__muted" data-moderation="recording" style={{ marginTop: logical(centreOffset(KIT_METRICS.button.height, SPINNER_SIZE)) }}>
          <PendingSpinner label={say("moderation.window.recordedWaiting")} />
        </span>
      ) : (
        <span className="ot-moderation__muted ot-moderation__small" style={{ marginTop: logical(centreOffset(KIT_METRICS.button.height, BODY_TYPE["body-sm"].lineHeight)) }}>
          {busy ? "" : self === null ? "" : say("moderation.window.recordedAs", { family: self })}
        </span>
      )}
      <span className="ot-moderation__footer-buttons">
        <Button disabled={busy} onClick={onCancel}>
          {say("moderation.window.cancel")}
        </Button>
        {danger ? (
          <Button variant="danger" busy={busy} busyLabel={say("moderation.window.recording")} onClick={onGo}>
            {go}
          </Button>
        ) : (
          <Button variant="primary" busy={busy} busyLabel={say("moderation.window.recording")} onClick={onGo}>
            {go}
          </Button>
        )}
      </span>
    </div>
  );
}

/**
 * When a length would end, as the form says it.
 *
 * @param action The timed action.
 * @param choice The length chosen or typed.
 * @param now The page's clock.
 * @param when Writes a moment.
 * @returns The sentence, or null when the length is not one the action takes.
 */
export function endsSentence(action: TimedAction, choice: string, now: number, when: (at: number) => string): string | null {
  const minutes = chosenMinutes(action, choice);
  if (minutes === null) {
    return null;
  }
  return minutes === 0 ? say("moderation.window.endsNever") : say("moderation.window.endsAt", { when: when(now + minutes * 60_000) });
}
